/**
 * 岩手県の料理12品。レベルが上がるほど大きく、器つきの汁物・鍋物になる。
 *
 * 選定のときに迷った点を残しておく。
 * - 「豆しとぎ」は青森と岩手の両方に伝わる。八戸・三戸の品として青森に置いたので、岩手からは外した。
 * - 「かっけ」も南部藩の領域をまたぐ。三角に切った平たい生地という形が青森と重なるため、
 *   二戸・久慈の「そばかっけ」として岩手に置き、青森からは外した。
 * - 「いもの子汁」は秋田の「いものこ汁」と、「どんこ汁」は福島のものと名前が重なるので、
 *   どちらも岩手には入れていない。汁物の枠は「まめぶ汁」と「ひっつみ」に絞った。
 * - 「豆腐田楽」は愛知の「味噌田楽」と見た目が近いので外した。
 * - わんこそばは「空いた椀を積み上げる」という描写が公的な出典に見当たらなかったので、
 *   椀を積まず、蓋を添えた小さな木椀が並ぶ姿にしている。
 * - 「ひっつみ」は麺ではなく、手でちぎった5センチ四方ほどの生地片である。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：しだみだんご・きりせんしょ・お茶もち・そばかっけ・がんづき・ひゅうず・氷頭なます・すき昆布の煮物・くるみ雑煮（店で食べられる品に差し替え）
 * - 北上コロッケ：誕生の年・考案者・B-1グランプリ出場：公式ページで確かめられなかったので書かない
 * - じゃじゃ麺：「盛岡三大麺」：盛岡観光コンベンション協会の三大麺ページは2回開いて2回とも404で、確かめられなかったので書かない
 * - じゃじゃ麺：盛岡で始めた店・人物：店名は書かない決まりで、公式でも確かめていないので書かない
 * - 一関もち膳：「もちの種類が全国一多い」：一関市観光協会のサイトに『とされており』の形であるが、出典の根拠が示されていないので書かない
 * - 一関もち膳：ユネスコ無形文化遺産「和食」に一関の餅食文化が含まれる：農水省ページにあるが、登録は『和食』全体なので誤解を避けて書かない
 * - 一関もち膳：冠婚葬祭の儀礼食「餅本膳」と、その作法（小笠原流・四条流、おとり餅役）：農水省ページにある。もち膳（店で出す膳）と混同されやすいので書かない
 * - 釜石ラーメン：最初に出した店・年：公式で確かめられなかったので書かない
 * - 釜石ラーメン：加盟店の数（hints に『のれんの会21店舗』）：公式で確かめておらず、変わる数字なので書かない
 * - 三陸の蒸しかき：食べ放題の値段：変わる情報なので書かない
 * - 三陸のあわび：生産量の全国順位（令和6年で第3位）：年ごとに変わる数字なので書かない
 * - 三陸のあわび：「いちご煮」：ページにあるが、三陸地域の別の料理であり、この品の説明から外した
 * - 三陸のあわび：焼き方（鉄板焼き・バター醤油など）：公式資料で確かめられなかったので書かない
 * - 岩手短角牛：「夏山冬里」の中身（夏は山に放牧、冬は里の牛舎）：出典に説明がないので言葉だけ使い、中身は書かない
 * - 三陸生うに丼：「世界三大漁場のひとつ三陸リアス海岸」：出典にあるが称号の根拠が示されていないので書かない
 * - 三陸生うに丼：瓶入り生ウニの相場：値段なので書かない
 * - 三陸生うに丼：「ミョウバンが入っていない」：特定商品の話なので書かない
 * - 前沢牛：「肉質日本一」：店紹介ページの宣伝文句なので書かない（奥州市の祭りのページにも「日本一の前沢牛」とあるが、何の日本一かの根拠が書かれていないので使わない）
 * - 前沢牛：「東の横綱」：農水省のページに「東の横綱『前沢牛』として全国的な知名度」とあるが、称号の扱いを避けて書かなかった。使うなら「東の横綱と呼ばれる」の伝聞形で書ける
 * - 前沢牛：「全国最多となる通算6回の名誉賞（農林水産大臣賞）」：農水省のページに書いてあるが、受賞歴は宣伝色が強いので外した。必要なら同ページを出典に書ける
 * - 前沢牛：ステーキ・鉄板焼きなどの食べ方：公式資料で確かめられなかったので書かない
  * - 2026-10-12 地元民レビューで「北上コロッケ」を外し「盛岡冷麺」を入れた（地元の人がまず挙げる品のため。旧IDは idMigrations の retiredItems）
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';
import { iwateSceneryTour } from './iwateScenes';
import { IWATE_FOOD_HULLS } from './iwateFoodShapes.generated';

function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'hittsumi',
    level: 1,
    name: 'ひっつみ',
    shortName: 'ひっつみ',
    image: '/assets/foods/iwate/01_hittsumi.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: '水でこねた小麦粉の生地を薄く伸ばし、手でちぎって煮る。「ひっつまむ」という方言がなまった名。米が不作の年に主食の代わりとして食べられた。生地は5センチ四方ほどで、ちぎり口が不定形になる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/28_1_iwate.html',
    icon: { label: 'ひっつみ', shape: 'pot', baseColor: '#c2a97e', accentColor: '#6b4a28' },
    reward: {
      unlockScore: 200,
      area: '盛岡市',
      headline: '手でちぎって落とす',
      tip: '地域によって「とってなげ」「はっと」「きりばっと」とも呼ばれる。冷蔵庫で1〜2時間寝かせた生地を、左手で持って右手で引っ張りながら薄くしてちぎる。',
      links: searchLinks('盛岡市', 'ひっつみ'),
    },
  },
  {
    id: 'mamebu-jiru',
    level: 2,
    name: 'まめぶ汁',
    shortName: 'まめぶ汁',
    image: '/assets/foods/iwate/02_mamebu_jiru.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: 'くるみを包んで丸めた小麦粉の団子を、昆布と煮干しの出汁で野菜といっしょに煮る。江戸時代の凶作のとき、麺の代わりに食べられたのが始まりと考えられている。団子が浮いてきたらできあがり。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/28_3_iwate.html',
    icon: { label: 'まめぶ汁', shape: 'hotpot', baseColor: '#b08a52', accentColor: '#f0ece0' },
    reward: {
      unlockScore: 500,
      area: '久慈市山形町',
      headline: '豆粒ほどの団子',
      tip: '名は「豆粒ほどの大きさ」から、また「忠実に健康で達者に暮らす」という願いから来ているという。慶事には大きな団子、弔事には小さな団子を使うとも言われる。',
      links: searchLinks('久慈市', 'まめぶ汁'),
    },
  },
  {
    id: 'morioka-jajamen',
    level: 3,
    name: 'じゃじゃ麺',
    shortName: 'じゃじゃ麺',
    image: '/assets/foods/iwate/03_morioka_jajamen.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: 'ゆでた熱い麺に肉味噌をかけ、きゅうりとねぎをのせる。好みでにんにくや生姜、ラー油、酢を加え、よく混ぜて食べる。中華料理の「炒醤麺」を岩手風にしたものといわれる。',
    source:
      'https://iwatetabi.jp/spots/16642/',
    icon: { label: 'じゃじゃ麺', shape: 'noodle-bowl', baseColor: '#efe6d0', accentColor: '#4a2e1e' },
    reward: {
      unlockScore: 1000,
      area: '盛岡市',
      headline: '麺に肉味噌を混ぜて食べる',
      tip: '食べ終えた器に卵を割り入れ、少しの肉味噌とゆで汁を加えると「鶏蛋湯（チータンタン）」というスープになる。',
      links: searchLinks('盛岡市', '盛岡じゃじゃ麺'),
      official: [{ label: 'いわての旅「盛岡じゃじゃ麺（盛岡市）」', url: 'https://iwatetabi.jp/spots/16642/' }],
    },
  },
  {
    id: 'morioka-reimen',
    level: 4,
    name: '盛岡冷麺',
    shortName: '盛岡冷麺',
    image: '/assets/foods/iwate/04_morioka_reimen.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '小麦粉とでんぷんで作る、強いコシのある透き通った麺が特徴。牛骨・鶏肉などを煮込んだスープに、ゆで卵やキュウリ、季節の果物をのせる。キムチの量で辛さを調節して食べる。',
    source:
      'https://www.city.morioka.iwate.jp/shisei/morioka_profile/1009300.html',
    icon: { label: '冷麺', shape: 'noodle-bowl', baseColor: '#ece6d8', accentColor: '#c8402a' },
    reward: {
      unlockScore: 1800,
      area: '盛岡市',
      headline: 'キムチの量で辛さを選ぶ冷たい麺',
      tip: '麺は小麦粉と片栗粉で作るため、強いコシがある。牛肉や鶏肉を煮込んでほどよく冷やしたスープが、キムチの辛みと合わさる。',
      links: searchLinks('盛岡市', '盛岡冷麺'),
      official: [{ label: '岩手県公式観光サイト いわての旅「盛岡冷麺」', url: 'https://iwatetabi.jp/spots/16606/' }],
    },
  },
  {
    id: 'ichinoseki-mochi-zen',
    level: 5,
    name: '一関もち膳',
    shortName: '一関もち膳',
    image: '/assets/foods/iwate/05_ichinoseki_mochi_zen.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '一関市を中心とする県南では、江戸時代に毎月1日と15日に餅をついて神仏に供える習わしができ、「餅暦」によって年に60回以上も餅を食べる。くるみ、枝豆、ごま、納豆など約300通りの食べ方がある。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/28_6_iwate.html',
    icon: { label: 'もち膳', shape: 'mochi', baseColor: '#f4f0e6', accentColor: '#7ab84a' },
    reward: {
      unlockScore: 3000,
      area: '一関市',
      headline: '年に60回以上餅を食べる土地',
      tip: '一関地方では、何種類ものもちを一度に味わえる膳が食べられる。8種類のおもちとお雑煮がそろう「果報餅膳」などがある。',
      links: searchLinks('一関市', '一関 もち膳'),
      official: [{ label: 'いわての旅「もち料理」（問い合わせ先：一関市商業観光課）', url: 'https://iwatetabi.jp/spots/16530/' }, { label: '一関市観光協会 いち旅「グルメ」', url: 'https://www.ichitabi.jp/gourmet/result.php?c=1' }],
    },
  },
  {
    id: 'kamaishi-ramen',
    level: 6,
    name: '釜石ラーメン',
    shortName: '釜石ラーメン',
    image: '/assets/foods/iwate/06_kamaishi_ramen.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '琥珀色に透き通った醤油味のあっさりしたスープに、極細の縮れ麺を合わせる。中華料理店や食堂、そば・うどん店でも出し、「釜石ラーメン」ののぼりが目印になっている。',
    source:
      'https://iwatetabi.jp/spots/66893/',
    icon: { label: '釜石ラーメン', shape: 'ramen', baseColor: '#c98a2a', accentColor: '#f0e2b0' },
    reward: {
      unlockScore: 4500,
      area: '釜石市',
      headline: '琥珀色のスープに極細麺',
      tip: '製鉄所で働く人や漁師など、おなかをすかせた人を待たせないように、早くゆで上がる極細麺が広まったといわれる。',
      links: searchLinks('釜石市', '釜石ラーメン'),
      official: [{ label: '釜石観光物産協会 かまなび「釜石ラーメン」', url: 'https://kamaishi-kankou.jp/gourmet/kamaishiramen/' }],
    },
  },
  {
    id: 'sanriku-mushigaki',
    level: 7,
    name: '三陸の蒸しかき',
    shortName: '三陸の蒸しかき',
    image: '/assets/foods/iwate/07_sanriku_mushigaki.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '三陸の山田町のかき小屋では、例年11月から6月ごろ、蒸し焼きにした牡蠣を味わえる。蒸し時間の10分を含めて40分の制限時間で食べる。かきの季節以外は、三陸や山田の海の幸を出している。',
    source:
      'https://iwatetabi.jp/spots/66689/',
    icon: { label: '蒸しかき', shape: 'sashimi-platter', baseColor: '#9a9a92', accentColor: '#f0e6c8' },
    reward: {
      unlockScore: 6500,
      area: '山田町',
      headline: 'かき小屋で蒸し焼きの牡蠣',
      links: searchLinks('山田町', '蒸し牡蠣'),
    },
  },
  {
    id: 'wanko-soba',
    level: 8,
    name: 'わんこそば',
    shortName: 'わんこそば',
    image: '/assets/foods/iwate/08_wanko_soba.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: '一口ぶんに小分けしたそばを、木のお椀で次々に振る舞う。「わんこ」は木地椀をさす方言。店によって違うが15杯前後でふつうのもりそば1杯ぶんになる。お椀に蓋をするまで続く。',
    source:
      'https://www.city.morioka.iwate.jp/shisei/morioka_profile/1009300.html',
    icon: { label: 'わんこそば', shape: 'noodle-bowl', baseColor: '#7a3a22', accentColor: '#efe6d2' },
    reward: {
      unlockScore: 9000,
      area: '盛岡市',
      headline: '15杯でもりそば1杯',
      tip: '全日本わんこそば選手権は、1986年の「日本めんサミット」の記念行事として始まった。時間制限のなかった頃の最高記録は559杯。',
      links: searchLinks('盛岡市', 'わんこそば'),
    },
  },
  {
    id: 'sanriku-awabi',
    level: 9,
    name: '三陸のあわび',
    shortName: '三陸のあわび',
    image: '/assets/foods/iwate/09_sanriku_awabi.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: 'コンブやワカメなどの海藻が豊富に生い茂る岩手県沿岸部で育ったアワビ。肉厚で歯応えが良いと評価される。生で食べるだけでなく、干鮑として中国や香港で最高級品として取引されている。',
    source:
      'https://www.pref.iwate.jp/kensei/profile/1000655/1016282.html',
    icon: { label: 'あわび', shape: 'sashimi-platter', baseColor: '#c9a050', accentColor: '#b8c8c8' },
    reward: {
      unlockScore: 12000,
      area: '岩手県沿岸部（三陸）',
      headline: '海藻の海で育つ肉厚のアワビ',
      tip: '11月から12月にかけ、波の穏やかな日に一斉に獲る「口開け」で漁獲する。資源を守るため、9cm以下のアワビは獲らない決まりがある。',
      links: searchLinks('陸前高田市', 'あわび'),
    },
  },
  {
    id: 'iwate-tankaku-gyu',
    level: 10,
    name: '岩手短角牛',
    shortName: '岩手短角牛',
    image: '/assets/foods/iwate/10_iwate_tankaku_gyu.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: '一般にいう霜降り肉とちがい、脂の少ない赤身が多く、肉本来のうまみがある牛肉。「夏山冬里」ですこやかに育てられる。岩泉町では「いわいずみ短角牛」として出している。',
    source:
      'https://iwatetabi.jp/spots/16694/',
    icon: { label: '短角牛', shape: 'feast', baseColor: '#9a2a2a', accentColor: '#5a3020' },
    reward: {
      unlockScore: 16000,
      area: '岩泉町ほか',
      headline: '霜降りでない赤身の牛',
      tip: '岩泉町の短角牛は、スローフード国際協会が選ぶ「食の世界遺産」に選ばれたとされる。脂肪の摂りすぎが気になる人にも勧められている。',
      links: searchLinks('岩手県', '短角牛'),
    },
  },
  {
    id: 'sanriku-nama-uni-don',
    level: 11,
    name: '三陸生うに丼',
    shortName: '三陸生うに丼',
    image: '/assets/foods/iwate/11_sanriku_nama_uni_don.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '天然のわかめや昆布を食べて育ったウニは上品な甘さがある。釜石では5月から8月中旬ごろに生ウニが店先に並び、殻つきウニや生ウニ丼で味わう。むきたての北紫ウニを海水と瓶に詰めた生ウニもある。',
    source:
      'https://travel-guide.kamaishi-dmc.com/uni/',
    icon: { label: 'うに丼', shape: 'rice-bowl', baseColor: '#e8a030', accentColor: '#f4d070' },
    reward: {
      unlockScore: 22000,
      area: '釜石市／洋野町',
      headline: '夏の三陸の生ウニをご飯に',
      tip: '洋野町ではウニが特産で、いちばん旬の時期に「たねいちウニまつり」が開かれる。町に伝わる潜水の技「南部もぐり」の実演もある。',
      links: searchLinks('洋野町', '生うに丼'),
    },
  },
  {
    id: 'maezawa-gyu',
    level: 12,
    name: '前沢牛',
    shortName: '前沢牛',
    image: '/assets/foods/iwate/12_maezawa_gyu.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '岩手県奥州市前沢区を生産地とする牛肉で、国の地理的表示（GI）に登録されている。上質できめ細やかな霜降りで、しっとりした肉質。脂はしつこくなく、上質な香りが口の中に広がる。',
    source:
      'https://www.maff.go.jp/j/shokusan/gi_act/register/0028/index.html',
    icon: { label: '前沢牛', shape: 'feast', baseColor: '#d87a7a', accentColor: '#f4e0d8' },
    reward: {
      unlockScore: 30000,
      area: '奥州市前沢区',
      headline: 'きめ細やかな霜降りの牛肉',
      tip: '前沢は雪解け水が豊富で米づくりに適し、良質で清潔な稲わらが得られる。昭和40年代に肥育を始め、地域が一丸となって生産技術を磨いてきた。',
      links: searchLinks('奥州市', '前沢牛'),
      official: [{ label: '奥州市「前沢牛まつり」', url: 'https://www.city.oshu.iwate.jp/soshiki/22/1154/235.html' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const iwateFoodStage: StageDefinition = {
  regionId: 'iwate',
  regionName: '岩手県',
  mode: 'food',
  title: '岩手めしマージ',
  theme: {
    primaryColor: '#2f3640',
    secondaryColor: '#e7ded0',
    accentColor: '#b8873f',
    backgroundColor: '#f7f2e8',
  },
  /** 背景ツアー「岩手めぐり」。軸は季節。5月の八幡平から始めて、翌年3月の高田松原で終わる。最後の一枚だけは静かに扱う。 */
  scenery: {
    tour: iwateSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, IWATE_FOOD_HULLS),
};
